import React, { useState } from 'react';
import { parseNutritionWithOpenAI, type MealNutritionEstimate } from '../../utils/openaiService';
import { saveMealToSupabase } from '../../utils/mealLogService';
import MealEstimateReviewModal, { type ReviewableMealEstimate } from './MealEstimateReviewModal';

interface MealLogPanelProps {
  selectedUserId: string;
  clientName: string;
  onMealLogged: () => void;
}

export default function MealLogPanel({ selectedUserId, clientName, onMealLogged }: MealLogPanelProps) {
  const [mealText, setMealText] = useState('');
  const [estimating, setEstimating] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualCalories, setManualCalories] = useState('');
  const [manualProtein, setManualProtein] = useState('');
  const [manualCarbs, setManualCarbs] = useState('');
  const [manualFat, setManualFat] = useState('');
  const [manualSaving, setManualSaving] = useState(false);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [pendingDescription, setPendingDescription] = useState('');
  const [pendingEstimate, setPendingEstimate] = useState<MealNutritionEstimate | null>(null);
  const [savingReview, setSavingReview] = useState(false);

  const resetForm = () => {
    setMealText('');
    setManualCalories('');
    setManualProtein('');
    setManualCarbs('');
    setManualFat('');
  };

  const prepareMealReview = async (description: string) => {
    setEstimating(true);
    try {
      const nutritionData = await parseNutritionWithOpenAI(description);
      setPendingDescription(description);
      setPendingEstimate(nutritionData);
      setReviewOpen(true);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to estimate meal';
      alert(message);
    } finally {
      setEstimating(false);
    }
  };

  const handleAISubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = mealText.trim();
    if (!trimmed || estimating) return;
    await prepareMealReview(trimmed);
  };

  const handleConfirmReview = async (finalEstimate: ReviewableMealEstimate) => {
    setSavingReview(true);
    try {
      await saveMealToSupabase(selectedUserId, pendingDescription, finalEstimate);
      setReviewOpen(false);
      setPendingEstimate(null);
      setPendingDescription('');
      resetForm();
      onMealLogged();
      alert(
        `Meal logged for ${clientName}:\n"${pendingDescription}"\n\n${Math.round(finalEstimate.calories)} calories`
      );
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to log meal';
      alert(message);
    } finally {
      setSavingReview(false);
    }
  };

  const handleCancelReview = () => {
    setReviewOpen(false);
    setPendingEstimate(null);
    setPendingDescription('');
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const calories = Number.parseFloat(manualCalories);
    if (!Number.isFinite(calories) || calories === 0) {
      alert('Enter valid calories (use negative for workouts).');
      return;
    }

    setManualSaving(true);
    try {
      const nutrition: ReviewableMealEstimate = {
        calories,
        protein_g: manualProtein ? Number.parseFloat(manualProtein) || 0 : 0,
        carbs_g: manualCarbs ? Number.parseFloat(manualCarbs) || 0 : 0,
        fat_g: manualFat ? Number.parseFloat(manualFat) || 0 : 0,
      };

      const description = mealText.trim() || (calories < 0 ? 'Workout' : 'Manual entry');
      await saveMealToSupabase(selectedUserId, description, nutrition);
      resetForm();
      setShowManualEntry(false);
      onMealLogged();
      alert(`Meal logged for ${clientName}.`);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to log meal';
      alert(message);
    } finally {
      setManualSaving(false);
    }
  };

  return (
    <>
      <div className="retro-card">
        <div className="index-tab">
          <h3 className="text-lg font-semibold text-text-dark">✨ Log a Meal</h3>
        </div>
        <div className="p-4 md:p-6 space-y-4">
          <p className="text-sm text-text-medium">
            Describe what {clientName} ate — AI will estimate macros for you to review before saving.
          </p>

          {!showManualEntry ? (
            <form onSubmit={handleAISubmit} className="space-y-3">
              <textarea
                value={mealText}
                onChange={(e) => setMealText(e.target.value)}
                placeholder='e.g. "Chicken and rice for dinner" or "30 min run burned 300 cal"'
                rows={3}
                className="vintage-input w-full resize-none"
                disabled={estimating}
              />
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="submit"
                  disabled={estimating || !mealText.trim()}
                  className="neon-button success flex-1 disabled:opacity-50"
                >
                  {estimating ? 'Estimating…' : '🤖 Estimate & Review'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowManualEntry(true)}
                  className="neon-button flex-1"
                >
                  Manual entry
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleManualSubmit} className="space-y-3">
              <input
                type="text"
                value={mealText}
                onChange={(e) => setMealText(e.target.value)}
                placeholder="Meal description (optional)"
                className="vintage-input w-full"
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-tag">Calories *</label>
                  <input
                    type="number"
                    value={manualCalories}
                    onChange={(e) => setManualCalories(e.target.value)}
                    placeholder="e.g. 650 or -300"
                    className="vintage-input w-full"
                    required
                  />
                </div>
                <div>
                  <label className="label-tag">Protein (g)</label>
                  <input
                    type="number"
                    value={manualProtein}
                    onChange={(e) => setManualProtein(e.target.value)}
                    className="vintage-input w-full"
                  />
                </div>
                <div>
                  <label className="label-tag">Carbs (g)</label>
                  <input
                    type="number"
                    value={manualCarbs}
                    onChange={(e) => setManualCarbs(e.target.value)}
                    className="vintage-input w-full"
                  />
                </div>
                <div>
                  <label className="label-tag">Fat (g)</label>
                  <input
                    type="number"
                    value={manualFat}
                    onChange={(e) => setManualFat(e.target.value)}
                    className="vintage-input w-full"
                  />
                </div>
              </div>
              <p className="text-xs text-text-medium">
                Use negative calories for workouts (e.g. -300). Macros optional.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="submit"
                  disabled={manualSaving}
                  className="neon-button success flex-1 disabled:opacity-50"
                >
                  {manualSaving ? 'Saving…' : 'Save manual entry'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowManualEntry(false)}
                  className="neon-button flex-1"
                >
                  Back to AI
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <MealEstimateReviewModal
        isOpen={reviewOpen}
        description={pendingDescription}
        estimate={pendingEstimate}
        confidence={pendingEstimate?.confidence ?? 'medium'}
        logging={savingReview}
        onConfirm={handleConfirmReview}
        onCancel={handleCancelReview}
      />
    </>
  );
}
